import { useMemo } from 'react';
import type { GridColDef, GridColumnVisibilityModel, GridValidRowModel } from '@mui/x-data-grid';
import type { ExportVariables } from '@eis/core';

/** One column of an export request. */
export type ExportColumn = NonNullable<ExportVariables['columns']>[number];

/** The cell-type hint of an export column (SDD-020 §8.2); without it the proto field type decides. */
export type ExportColumnType = 'TEXT' | 'NUMBER' | 'MONEY' | 'DATE' | 'DATETIME' | 'BOOLEAN';

/**
 * A grid column that can be exported: `exportPath` is the protojson path of the value in the list
 * item (`policy.code`, `snapshot.validFrom`, `data.frame_number`); `exportType` the cell type.
 * A column without `exportPath` (actions, chips with no data of their own) is not exported.
 */
export type ExportableGridColDef<Row extends GridValidRowModel> = GridColDef<Row> & {
  readonly exportPath?: string;
  readonly exportType?: ExportColumnType;
};

/**
 * The export's `columns[]` from the grid's VISIBLE columns, in grid order, with their translated
 * headers (SDD-020 §9) — what the user sees is what the file holds.
 */
export function gridExportColumns<Row extends GridValidRowModel>(
  columns: readonly ExportableGridColDef<Row>[],
  visibility: GridColumnVisibilityModel = {},
): ExportColumn[] {
  return columns.flatMap((column) => {
    if (column.exportPath === undefined || visibility[column.field] === false) return [];
    return [
      {
        path: column.exportPath,
        header: column.headerName ?? column.field,
        ...(column.exportType === undefined ? {} : { type: column.exportType }),
      },
    ];
  });
}

/** The hook form of {@link gridExportColumns}, memoised on the columns. */
export function useGridExportColumns<Row extends GridValidRowModel>(
  columns: readonly ExportableGridColDef<Row>[],
  visibility?: GridColumnVisibilityModel,
): ExportColumn[] {
  return useMemo(() => gridExportColumns(columns, visibility), [columns, visibility]);
}
